// UI language: English by default, Vietnamese on request. English text is the key; vi.ts holds the translations.
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { VI } from "./vi";

export type Lang = "en" | "vi";
const KEY = "orderai:lang";

let current: Lang = (() => {
  try {
    return localStorage.getItem(KEY) === "vi" ? "vi" : "en";
  } catch {
    return "en";
  }
})();

const missing = new Set<string>();

/** Translate an English UI string. `{name}` placeholders are filled from vars. */
export function tr(en: string, vars?: Record<string, string | number>): string {
  let s = en;
  if (current === "vi") {
    const v = VI[en];
    if (v) s = v;
    else if (import.meta.env.DEV && !missing.has(en)) {
      missing.add(en);
      console.warn(`[i18n] missing vi: ${JSON.stringify(en)}`);
    }
  }
  return vars ? s.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? `{${k}}`)) : s;
}

export const currentLang = () => current;

const Ctx = createContext<{ lang: Lang; setLang: (l: Lang) => void }>({ lang: current, setLang: () => {} });

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setState] = useState<Lang>(current);
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);
  const setLang = useCallback((l: Lang) => {
    current = l;
    try {
      localStorage.setItem(KEY, l);
    } catch {
      /* ignore */
    }
    setState(l);
  }, []);
  // Remount the tree on change so every tr() call re-runs.
  return (
    <Ctx.Provider value={{ lang, setLang }}>
      <div key={lang} className="contents">
        {children}
      </div>
    </Ctx.Provider>
  );
}

export const useLang = () => useContext(Ctx);
